import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../Header';
import './PrivacyPage.css';

const PrivacyPage = () => {
  return (
    <div className="privacy-page">
      <Header 
        pageClass="privacy-header"
        rightButton={
          <Link to="/" className="nav-link contact-btn">
            Back to Home
          </Link>
        }
      />
      
      <main className="privacy-content">
        <div className="privacy-container">
          <h1>Privacy Policy</h1>
          <p className="last-updated"><strong>Last Updated: December 2025</strong></p>

          <section className="privacy-section">
            <h2>1. Introduction</h2>
            <p>
              TrackCalAI ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application ("App"). Please read this Privacy Policy carefully. If you do not agree with the terms of this Privacy Policy, please do not access the App.
            </p>
          </section>

          <section className="privacy-section">
            <h2>2. Information We Collect</h2>
            
            <h3>2.1 Personal Information</h3>
            <p>We collect personal information that you provide directly to us:</p>
            
            <p><strong>Account Information:</strong></p>
            <ul>
              <li>Name and email address</li>
              <li>Age and date of birth</li>
              <li>Gender and height</li>
              <li>Weight and body measurements</li>
              <li>Fitness goals and preferences</li>
              <li>Dietary restrictions and preferences</li>
            </ul>

            <p><strong>Authentication Information:</strong></p>
            <ul>
              <li>Email and password (for email/password accounts)</li>
              <li>Apple Sign-In data (when using Apple authentication)</li>
              <li>Google Sign-In data (when using Google authentication)</li>
              <li>Account creation and login timestamps</li>
            </ul>

            <h3>2.2 Health and Fitness Data</h3>
            
            <p><strong>Physical Measurements:</strong></p>
            <ul>
              <li>Current weight and weight history</li>
              <li>Height and body measurements</li>
              <li>Progress photos (if uploaded)</li>
              <li>Workout frequency and intensity</li>
              <li>Fitness level and activity preferences</li>
            </ul>

            <p><strong>Nutritional Data:</strong></p>
            <ul>
              <li>Food photos taken through the app</li>
              <li>Text entries of food items</li>
              <li>Meal history and nutritional intake</li>
              <li>Macro and micronutrient tracking</li>
              <li>Recipe preferences and cooking habits</li>
            </ul>

            <h3>2.3 AI Analysis Data</h3>
            
            <p><strong>Food Recognition Data:</strong></p>
            <ul>
              <li>Photos of food items for AI analysis</li>
              <li>Text descriptions of food items</li>
              <li>Portion size estimates and corrections</li>
              <li>Nutritional analysis results</li>
              <li>Recipe generation requests and results</li>
            </ul>

            <p><strong>Enhanced Analysis Data:</strong></p>
            <ul>
              <li>User corrections to AI analysis</li>
              <li>Manual adjustments to food quantities</li>
              <li>Custom food item additions</li>
              <li>Accuracy feedback and improvements</li>
            </ul>

            <h3>2.4 Usage Data</h3>
            
            <p><strong>App Interaction Data:</strong></p>
            <ul>
              <li>Features used and frequency of use</li>
              <li>Time spent in different app sections</li>
              <li>Navigation patterns and user flows</li>
              <li>Feature preferences and settings</li>
              <li>Error logs and crash reports</li>
            </ul>

            <p><strong>Device Information:</strong></p>
            <ul>
              <li>Device type, model, and operating system</li>
              <li>App version and installation date</li>
              <li>Device settings and permissions</li>
              <li>Network information and connection type</li>
              <li>Unique device identifiers</li>
            </ul>

            <h3>2.5 Camera and Photo Data</h3>
            
            <p><strong>Photo Collection:</strong></p>
            <ul>
              <li>Food photos taken through the app camera</li>
              <li>Photos stored locally on your device</li>
              <li>Photos processed for AI analysis</li>
              <li>Photo metadata (timestamp, location if enabled)</li>
            </ul>

            <p><strong>Camera Permissions:</strong></p>
            <ul>
              <li>Camera access for food photography</li>
              <li>Photo library access for selecting existing photos</li>
              <li>Storage permissions for saving photos</li>
            </ul>

            <h3>2.6 Advertising and Marketing Data</h3>
            
            <p><strong>Event Tracking:</strong></p>
            <ul>
              <li>App installation events</li>
              <li>User registration events</li>
              <li>Subscription purchase events</li>
              <li>Onboarding completion events</li>
              <li>Feature usage events</li>
            </ul>

            <p><strong>Data Shared with Meta (Facebook):</strong></p>
            <ul>
              <li>Hashed email addresses (SHA256)</li>
              <li>Hashed phone numbers (SHA256)</li>
              <li>User IDs (anonymized)</li>
              <li>IP addresses (for fraud prevention and attribution)</li>
              <li>User Agent strings (browser/device information)</li>
              <li>Event timestamps</li>
              <li>Purchase amounts and currency</li>
              <li>Event types (registration, purchase, etc.)</li>
              <li>No personally identifiable information is shared in plain text (except IP address and User Agent, which are required for fraud prevention and ad attribution)</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>3. How We Use Your Information</h2>
            
            <h3>3.1 Service Provision</h3>
            
            <p><strong>Core App Features:</strong></p>
            <ul>
              <li>Provide nutrition tracking and analysis</li>
              <li>Generate personalized recommendations</li>
              <li>Create recipes based on available ingredients</li>
              <li>Track progress toward fitness goals</li>
              <li>Manage inventory and meal planning</li>
            </ul>

            <p><strong>AI Analysis Services:</strong></p>
            <ul>
              <li>Process food photos for nutritional analysis</li>
              <li>Enhance text-based food entries</li>
              <li>Generate recipe suggestions</li>
              <li>Improve AI accuracy through user feedback</li>
              <li>Provide portion size estimates</li>
            </ul>

            <h3>3.2 Personalization</h3>
            
            <p><strong>Customized Experience:</strong></p>
            <ul>
              <li>Tailor recommendations to your preferences</li>
              <li>Adjust macro goals based on your profile</li>
              <li>Provide relevant recipe suggestions</li>
              <li>Customize the user interface</li>
              <li>Remember your settings and preferences</li>
            </ul>

            <p><strong>Health Insights:</strong></p>
            <ul>
              <li>Track progress toward your goals</li>
              <li>Identify patterns in your eating habits</li>
              <li>Provide nutritional insights and recommendations</li>
              <li>Suggest improvements to your diet</li>
              <li>Monitor adherence to dietary restrictions</li>
            </ul>

            <h3>3.3 Communication</h3>
            
            <p><strong>App Notifications:</strong></p>
            <ul>
              <li>Meal reminders and notifications</li>
              <li>Progress updates and achievements</li>
              <li>Feature updates and new content</li>
              <li>Important account and security notifications</li>
            </ul>

            <p><strong>Support and Service:</strong></p>
            <ul>
              <li>Respond to your inquiries and support requests</li>
              <li>Provide technical assistance</li>
              <li>Send important updates about the service</li>
              <li>Communicate about account changes</li>
            </ul>

            <h3>3.4 Improvement and Analytics</h3>
            
            <p><strong>Service Improvement:</strong></p>
            <ul>
              <li>Analyze usage patterns to improve features</li>
              <li>Enhance AI accuracy and performance</li>
              <li>Develop new features and functionality</li>
              <li>Optimize app performance and user experience</li>
              <li>Conduct research and development</li>
            </ul>

            <p><strong>Analytics and Insights:</strong></p>
            <ul>
              <li>Understand how users interact with the app</li>
              <li>Identify popular features and areas for improvement</li>
              <li>Measure the effectiveness of AI analysis</li>
              <li>Generate anonymized usage statistics</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>4. Information Sharing and Disclosure</h2>
            
            <h3>4.1 We Do Not Sell Your Personal Information</h3>
            <p>TrackCalAI does not sell, rent, or trade your personal information to third parties for monetary consideration.</p>

            <h3>4.2 Service Providers</h3>
            <p>We may share your information with trusted third-party service providers who assist us in operating the App:</p>
            
            <p><strong>Cloud Storage Providers:</strong></p>
            <ul>
              <li>Store and manage your data securely</li>
              <li>Provide backup and disaster recovery services</li>
              <li>Ensure data availability and reliability</li>
            </ul>

            <p><strong>AI and Machine Learning Services:</strong></p>
            <ul>
              <li>Process food photos for nutritional analysis</li>
              <li>Enhance text-based food recognition</li>
              <li>Improve recipe generation algorithms</li>
              <li>Provide natural language processing capabilities</li>
            </ul>

            <p><strong>Analytics Providers:</strong></p>
            <ul>
              <li>Analyze app usage and performance</li>
              <li>Track user engagement and retention</li>
              <li>Identify technical issues and bugs</li>
              <li>Provide insights for product improvement</li>
            </ul>

            <p><strong>Advertising and Marketing Services:</strong></p>
            <ul>
              <li>Meta (Facebook) Conversions API for ad optimization</li>
              <li>Track conversion events (subscriptions, registrations, etc.)</li>
              <li>Optimize advertising campaigns</li>
              <li>Measure advertising effectiveness</li>
              <li>All personal information is hashed before sharing with Meta</li>
            </ul>

            <p><strong>Payment Processors:</strong></p>
            <ul>
              <li>Process subscription payments</li>
              <li>Handle billing and invoicing</li>
              <li>Manage refunds and cancellations</li>
              <li>Ensure secure payment transactions</li>
            </ul>

            <h3>4.3 Legal Requirements</h3>
            <p>We may disclose your information if required by law or in response to:</p>
            
            <p><strong>Legal Process:</strong></p>
            <ul>
              <li>Court orders, subpoenas, or legal requests</li>
              <li>Government investigations or inquiries</li>
              <li>Regulatory compliance requirements</li>
              <li>Law enforcement requests</li>
            </ul>

            <p><strong>Protection of Rights:</strong></p>
            <ul>
              <li>Protect our rights, property, or safety</li>
              <li>Protect the rights, property, or safety of our users</li>
              <li>Prevent fraud or illegal activities</li>
              <li>Enforce our Terms of Service</li>
            </ul>

            <h3>4.4 Business Transfers</h3>
            <p>In the event of a merger, acquisition, or sale of assets:</p>
            <ul>
              <li>Your information may be transferred to the new entity</li>
              <li>You will be notified of any material changes</li>
              <li>Your privacy rights will be maintained</li>
              <li>You may opt out of data transfer in some cases</li>
            </ul>

            <h3>4.5 User Consent</h3>
            <p>We may share your information with your explicit consent:</p>
            <ul>
              <li>When you choose to share data with third-party apps</li>
              <li>When you participate in research studies</li>
              <li>When you opt into promotional communications</li>
              <li>When you request data sharing for specific purposes</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>5. Data Security</h2>
            
            <h3>5.1 Security Measures</h3>
            <p>We implement appropriate technical and organizational measures to protect your information:</p>
            
            <p><strong>Technical Safeguards:</strong></p>
            <ul>
              <li>Encryption of data in transit and at rest</li>
              <li>Secure authentication and access controls</li>
              <li>Regular security audits and assessments</li>
              <li>Monitoring for unauthorized access attempts</li>
            </ul>

            <p><strong>Administrative Safeguards:</strong></p>
            <ul>
              <li>Limited access to personal information</li>
              <li>Employee training on data protection</li>
              <li>Regular security awareness programs</li>
              <li>Incident response and breach notification procedures</li>
            </ul>

            <h3>5.2 Data Retention</h3>
            
            <p><strong>Retention Periods:</strong></p>
            <ul>
              <li>Account data: Retained while your account is active</li>
              <li>Usage data: Retained for up to 2 years</li>
              <li>AI analysis data: Retained for up to 1 year</li>
              <li>Deleted data: Permanently removed within 30 days</li>
            </ul>

            <p><strong>Retention Purposes:</strong></p>
            <ul>
              <li>Provide ongoing service and support</li>
              <li>Comply with legal and regulatory requirements</li>
              <li>Improve AI models and service quality</li>
              <li>Resolve disputes and enforce agreements</li>
            </ul>

            <h3>5.3 Data Breach Response</h3>
            <p>In the event of a data breach:</p>
            <ul>
              <li>We will notify affected users within 72 hours</li>
              <li>We will report breaches to relevant authorities</li>
              <li>We will take immediate steps to secure systems</li>
              <li>We will provide guidance on protective measures</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>6. Your Privacy Rights</h2>
            
            <h3>6.1 Access and Portability</h3>
            
            <p><strong>Right to Access:</strong></p>
            <ul>
              <li>Request a copy of your personal information</li>
              <li>View the data we have collected about you</li>
              <li>Understand how your data is being used</li>
              <li>Receive data in a portable format</li>
            </ul>

            <p><strong>Data Portability:</strong></p>
            <ul>
              <li>Export your data in a machine-readable format</li>
              <li>Transfer your data to another service</li>
              <li>Maintain control over your personal information</li>
              <li>Ensure continuity of service</li>
            </ul>

            <h3>6.2 Correction and Updates</h3>
            
            <p><strong>Right to Rectification:</strong></p>
            <ul>
              <li>Correct inaccurate personal information</li>
              <li>Update outdated information</li>
              <li>Complete incomplete data</li>
              <li>Ensure data accuracy and relevance</li>
            </ul>

            <p><strong>Account Management:</strong></p>
            <ul>
              <li>Update your profile information</li>
              <li>Modify your preferences and settings</li>
              <li>Change your communication preferences</li>
              <li>Manage your subscription and billing</li>
            </ul>

            <h3>6.3 Deletion and Restriction</h3>
            
            <p><strong>Right to Erasure:</strong></p>
            <ul>
              <li>Request deletion of your personal information</li>
              <li>Remove your account and associated data</li>
              <li>Delete specific data categories</li>
              <li>Ensure permanent data removal</li>
            </ul>

            <p><strong>Right to Restriction:</strong></p>
            <ul>
              <li>Limit how we process your data</li>
              <li>Restrict certain data uses</li>
              <li>Suspend data processing temporarily</li>
              <li>Maintain data for legal purposes</li>
            </ul>

            <h3>6.4 Objection and Withdrawal</h3>
            
            <p><strong>Right to Object:</strong></p>
            <ul>
              <li>Object to processing for marketing purposes</li>
              <li>Opt out of automated decision-making</li>
              <li>Refuse certain data uses</li>
              <li>Maintain control over your data</li>
            </ul>

            <p><strong>Consent Withdrawal:</strong></p>
            <ul>
              <li>Withdraw consent for data processing</li>
              <li>Opt out of communications</li>
              <li>Disable certain app features</li>
              <li>Maintain essential service functionality</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>7. Children's Privacy</h2>
            
            <h3>7.1 Age Requirements</h3>
            
            <p><strong>Minimum Age:</strong></p>
            <ul>
              <li>Users must be at least 13 years old to use the App</li>
              <li>Users under 13 are not permitted to create accounts</li>
              <li>We do not knowingly collect data from children under 13</li>
            </ul>

            <p><strong>Parental Consent:</strong></p>
            <ul>
              <li>Users between 13-18 may need parental consent</li>
              <li>Parents can request deletion of their child's data</li>
              <li>We provide tools for parental oversight and control</li>
            </ul>

            <h3>7.2 Protection Measures</h3>
            
            <p><strong>Child Safety:</strong></p>
            <ul>
              <li>Implement age verification measures</li>
              <li>Provide parental control features</li>
              <li>Monitor for inappropriate content</li>
              <li>Educate users about online safety</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>8. International Data Transfers</h2>
            
            <h3>8.1 Cross-Border Transfers</h3>
            
            <p><strong>Data Transfers:</strong></p>
            <ul>
              <li>Your data may be transferred to countries outside your residence</li>
              <li>We ensure adequate protection for international transfers</li>
              <li>We comply with applicable data protection laws</li>
              <li>We implement appropriate safeguards for transfers</li>
            </ul>

            <p><strong>Protection Measures:</strong></p>
            <ul>
              <li>Standard contractual clauses for data transfers</li>
              <li>Adequacy decisions by relevant authorities</li>
              <li>Binding corporate rules and certifications</li>
              <li>User consent for specific transfers</li>
            </ul>

            <h3>8.2 Regional Compliance</h3>
            
            <p><strong>GDPR Compliance (EU):</strong></p>
            <ul>
              <li>Comply with General Data Protection Regulation</li>
              <li>Provide enhanced privacy rights for EU users</li>
              <li>Implement privacy by design principles</li>
              <li>Maintain data protection impact assessments</li>
            </ul>

            <p><strong>CCPA Compliance (California):</strong></p>
            <ul>
              <li>Comply with California Consumer Privacy Act</li>
              <li>Provide specific rights for California residents</li>
              <li>Maintain transparency about data practices</li>
              <li>Implement opt-out mechanisms</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>9. Cookies and Tracking Technologies</h2>
            
            <h3>9.1 App Analytics</h3>
            
            <p><strong>Usage Tracking:</strong></p>
            <ul>
              <li>Track app usage and performance</li>
              <li>Monitor feature adoption and engagement</li>
              <li>Analyze user behavior and preferences</li>
              <li>Improve app functionality and design</li>
            </ul>

            <p><strong>Performance Monitoring:</strong></p>
            <ul>
              <li>Monitor app crashes and errors</li>
              <li>Track loading times and performance</li>
              <li>Identify technical issues and bugs</li>
              <li>Optimize app speed and reliability</li>
            </ul>

            <h3>9.2 Third-Party Analytics</h3>
            
            <p><strong>Analytics Providers:</strong></p>
            <ul>
              <li>Google Analytics for Firebase</li>
              <li>App Store Connect Analytics</li>
              <li>Google Play Console Analytics</li>
              <li>Custom analytics and monitoring tools</li>
            </ul>

            <p><strong>Tracking Controls:</strong></p>
            <ul>
              <li>Opt-out options for analytics tracking</li>
              <li>Privacy controls in device settings</li>
              <li>App-specific privacy preferences</li>
              <li>Clear information about tracking practices</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>10. Changes to This Privacy Policy</h2>
            
            <h3>10.1 Policy Updates</h3>
            
            <p><strong>Notification of Changes:</strong></p>
            <ul>
              <li>We will notify you of material changes to this Privacy Policy</li>
              <li>Changes will be posted in the App and on our website</li>
              <li>We will provide advance notice of significant changes</li>
              <li>Continued use constitutes acceptance of updated policy</li>
            </ul>

            <p><strong>Version Control:</strong></p>
            <ul>
              <li>We maintain version history of privacy policy changes</li>
              <li>Previous versions are available upon request</li>
              <li>Changes are clearly marked with effective dates</li>
              <li>Users can review changes before they take effect</li>
            </ul>

            <h3>10.2 User Rights</h3>
            
            <p><strong>Consent to Changes:</strong></p>
            <p>By continuing to use the App after changes to this Privacy Policy, you acknowledge and agree to the updated terms.</p>
          </section>
        </div>
      </main>
      
      <footer className="footer-banner">
        <div className="footer-content">
          <p>&copy; 2025 SparkLabs LLC. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPage;

